import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import styles from './app.module.css';
import { DownloadRawReport } from './components/download-raw-report/DownloadRawReport';
import { FooterByAppType } from './components/footer-by-app-type/FooterByAppType';
import { HeaderByAppType } from './components/header-by-app-type/HeaderByAppType';
import * as AppUrls from './components/navbar/app.urls';
import { ZOOM_APP_URLS } from './components/navbar/app.urls';
import { LivePollEditorNavbar } from './components/navbar/livepoll-editor-navbar';
import { PrimaryNavbar } from './components/navbar/primary-navbar';
import { LivePollListScreenForZoom } from './meetings-apps/zoom-app/components/list-screen/LivePollListScreenForZoom';
import { TakeLivePollForZoom } from './meetings-apps/zoom-app/components/take-livepoll/take-livepoll-for-zoom';
import { CreateQuizFromTemplate } from './screens/livepoll-editor-from-template/create-quiz-from-template.screen';
import { LivePollEditorAndAnalytics } from './screens/livepoll-editor/livepoll-editor-analytics';
import { GoogleSlidesLivePollListScreen } from './screens/livepoll-list/GoogleSlidesLivePollListScreen';
import { LivePollListScreen } from './screens/livepoll-list/LivePollListScreen';
import ErrorBoundary from './wick-ui/error/ErrorBoundary';
import { OtherErrorBoundary } from './wick-ui/error/OtherErrorBoundary';
import React from 'react';
import { fetchAccountAndHeaderInfo } from './hooks/store-hooks';

const DashboardScreen = lazy(
  () => import('./screens/report-dashboard/DashboardScreen'),
);
const PermanentRoomDashboardScreen = lazy(
  () => import('./screens/permanent-room/PermanentRoomDashboardScreen'),
);
const SessionListScreen = lazy(
  () => import('./screens/session-list/SessionListScreen'),
);
const LivePollFinishedScreen = lazy(
  () =>
    import(
      './screens/run-livepoll/components/livepoll-finished/LivePollFinishedScreen'
    ),
);
const RunLivePollSessionScreen = lazy(
  () => import('./screens/run-livepoll/run-livepoll-session-screen'),
);
const LivePollDesignScreen = lazy(
  () => import('./screens/livepoll-design/LivePollDesign.screen'),
);
const LivePollEditorScreen = lazy(
  () => import('./screens/livepoll-editor/livepoll-editor.screen'),
);
const PerformanceLogScreen = lazy(
  () => import('./screens/performace-logs/PerformanceLogScreen'),
);
const PresenterNotesWindow = lazy(
  () => import('./screens/run-livepoll/components/PresenterNotesWindow'),
);
const GoogleSlidesLoginSuccessScreen = lazy(
  () => import('./screens/google-slides/GoogleSlidesLoginSuccess.screen'),
);

const getTemplateUidFromParamsOrLocalStorageOrNull = () => {
  const search = window.location.search;
  const params = new URLSearchParams(search);
  let templateUid = params.get('livePollsTemplateUID');

  if (!templateUid) {
    templateUid = localStorage.getItem('templateUid');
  }
  return templateUid;
};

export const AuthenticatedApp = () => {
  const [myAccount, setMyAccount] = React.useState(undefined);
  const [productCategories, setProductCategories] = React.useState(null);
  const [licenseName, setLicenseName] = React.useState<string | null>(null);
  React.useEffect(() => {
    const fetchData = async () => {
      const { account, categories } = await fetchAccountAndHeaderInfo();
      setMyAccount(account);
      setProductCategories(categories);
      const license = account?.license;
      setLicenseName(license?.subtitle || '');
    };
    if (!myAccount || !productCategories) {
      fetchData();
    }
  }, [myAccount, productCategories]);

  const templateUid = getTemplateUidFromParamsOrLocalStorageOrNull();
  return (
    <div className={styles.appContainer}>
      <div className={styles.navbarContainer}>
        <HeaderByAppType
          myAccount={myAccount}
          productCategories={productCategories}
        />
        <PrimaryNavbar />
        <LivePollEditorNavbar />
      </div>

      <div className={styles.mainContainer}>
        <Routes>
          <Route
            path={AppUrls.URLS.EDIT_LIVE_POLL_URL}
            element={
              <OtherErrorBoundary>
                <LivePollEditorAndAnalytics />
              </OtherErrorBoundary>
            }
          >
            <Route
              path={AppUrls.LIVEPOLL_EDITOR_SCREEN_RELATIVE_URLS.DASHBOARD}
              element={
                <OtherErrorBoundary>
                  <DashboardScreen />
                </OtherErrorBoundary>
              }
            />
            <Route
              path={AppUrls.LIVEPOLL_EDITOR_SCREEN_RELATIVE_URLS.SESSIONS}
              element={
                <OtherErrorBoundary>
                  <SessionListScreen />
                </OtherErrorBoundary>
              }
            />
            <Route
              path={AppUrls.LIVEPOLL_EDITOR_SCREEN_RELATIVE_URLS.DESIGN}
              element={
                <OtherErrorBoundary>
                  <LivePollDesignScreen />
                </OtherErrorBoundary>
              }
            />
            <Route
              path={
                AppUrls.LIVEPOLL_EDITOR_SCREEN_RELATIVE_URLS
                  .GOOGLE_SLIDES_EDITOR
              }
              element={
                <OtherErrorBoundary>
                  <LivePollEditorScreen />
                </OtherErrorBoundary>
              }
            />
            <Route
              path={
                AppUrls.LIVEPOLL_EDITOR_SCREEN_RELATIVE_URLS
                  .GOOGLE_SLIDES_DESIGN
              }
              element={
                <OtherErrorBoundary>
                  <LivePollDesignScreen />
                </OtherErrorBoundary>
              }
            />
            <Route
              index={true}
              element={
                <OtherErrorBoundary>
                  <LivePollEditorScreen />
                </OtherErrorBoundary>
              }
            />
          </Route>
          <Route
            path={AppUrls.URLS.PERMANENT_ROOM_SESSION_DASHBOARD}
            element={
              <OtherErrorBoundary>
                <PermanentRoomDashboardScreen />
              </OtherErrorBoundary>
            }
          />

          <Route
            path={AppUrls.URLS.LIVE_POLL_EDITOR_FROM_TEMPLATE}
            element={
              <OtherErrorBoundary>
                <CreateQuizFromTemplate />
              </OtherErrorBoundary>
            }
          />
          <Route
            path={AppUrls.URLS.FINISH_TAKE_LIVE_POLL_URL}
            element={
              <OtherErrorBoundary>
                <LivePollFinishedScreen />
              </OtherErrorBoundary>
            }
          />
          <Route
            path={AppUrls.URLS.TAKE_LIVE_POLL_URL}
            element={
              <ErrorBoundary componentName="RunLivePollSessionScreen">
                <RunLivePollSessionScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path={AppUrls.URLS.TAKE_LIVE_POLL_FOR_SLIDES_URL}
            element={
              <ErrorBoundary componentName="RunLivePollSessionScreen">
                <RunLivePollSessionScreen />
              </ErrorBoundary>
            }
          />
          <Route
            caseSensitive={true}
            path={AppUrls.URLS.LIVE_POLL_LIST_URL}
            element={
              <OtherErrorBoundary>
                <LivePollListScreen />
              </OtherErrorBoundary>
            }
          />

          <Route
            caseSensitive={true}
            path={AppUrls.URLS.LIVE_POLL_LIST_FOR_SLIDES_URL}
            element={
              <OtherErrorBoundary>
                <GoogleSlidesLivePollListScreen />
              </OtherErrorBoundary>
            }
          />

          <Route
            path={AppUrls.URLS.ADMIN_PERFORMANCE_LOGS_URL}
            element={
              <OtherErrorBoundary>
                <PerformanceLogScreen />
              </OtherErrorBoundary>
            }
          />

          <Route
            path={AppUrls.URLS.PRESENTER_VIEW_WINDOW_URL}
            element={
              <OtherErrorBoundary>
                <PresenterNotesWindow />
              </OtherErrorBoundary>
            }
          />
          <Route
            path={AppUrls.URLS.GOOGLE_SLIDES_LOGIN_SUCCESS}
            element={
              <OtherErrorBoundary>
                <GoogleSlidesLoginSuccessScreen />
              </OtherErrorBoundary>
            }
          />

          {templateUid && (
            <Route
              path="/"
              element={
                <Navigate
                  to={AppUrls.URLS.LIVE_POLL_EDITOR_FROM_TEMPLATE}
                  state={templateUid}
                  replace
                />
              }
            ></Route>
          )}

          {/* Zoom Ap Routes */}
          <Route
            path={ZOOM_APP_URLS.LIST_SCREEN}
            element={
              <OtherErrorBoundary>
                <LivePollListScreenForZoom />
              </OtherErrorBoundary>
            }
          />
          <Route
            path={ZOOM_APP_URLS.TAKE_LIVEPOLL}
            element={
              <OtherErrorBoundary>
                <TakeLivePollForZoom />
              </OtherErrorBoundary>
            }
          />

          <Route
            path="*"
            element={
              <Navigate to={`/${AppUrls.URLS.LIVE_POLL_LIST_URL}`} replace />
            }
          />
        </Routes>
      </div>

      <DownloadRawReport />
      <FooterByAppType licenseName={licenseName} />
    </div>
  );
};
